import React from "react";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import type { DryBulkDecision } from "@services/dry-bulk-nomination/types";
import { useTranslation } from "react-i18next";
import { formatUaeDateTime } from "../utils/formatUaeDate";
import AttachmentFileLink from "./AttachmentFileLink";

const LANG_BASE = "vessel-nomination.dry-bulk.review.decision";

interface Props {
  decision: DryBulkDecision;
  isOpen: boolean;
  onClose: () => void;
}

const Row: React.FC<{ testId: string; label: string; children: React.ReactNode }> = ({
  testId,
  label,
  children,
}) => (
  <div className="flex flex-col gap-1" data-testid={testId}>
    <span className="text-xs text-gray-500">{label}</span>
    <span className="text-sm whitespace-pre-wrap break-words">{children}</span>
  </div>
);

/** "View details" of an Approved / Rejected nomination (wireframe 09-7, VN-23): when and by whom, berth, comments / reason, files; Cancel and Ok close it. */
const DecisionDetailsDrawer: React.FC<Props> = ({ decision, isOpen, onClose }) => {
  const { t } = useTranslation();
  const approved = decision.decision === "APPROVED";
  const files = decision.attachments ?? [];

  return (
    <div data-testid="dry-bulk-decision-details">
      <SideDrawer
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={onClose}
        submitLabel={t(`${LANG_BASE}.ok`)}
        cancelLabel={t("common.cancel")}
        title={t(`${LANG_BASE}.${approved ? "title-approved" : "title-rejected"}`)}
      >
        <div className="w-[400px] p-6 flex flex-col gap-4">
          <div className="flex items-center justify-between gap-4 text-sm">
            <span data-testid="dry-bulk-decision-decided-at">
              {formatUaeDateTime(decision.decidedAt)}
            </span>
            <span data-testid="dry-bulk-decision-decided-by">
              {t(`${LANG_BASE}.submitted-by`)} {decision.decidedByName || decision.decidedBy || "-"}
            </span>
          </div>
          {approved && (
            <Row testId="dry-bulk-decision-berth-value" label={t(`${LANG_BASE}.berth`)}>
              {[decision.expectedBerthCode, decision.berthName].filter(Boolean).join(" – ") || "-"}
            </Row>
          )}
          <Row
            testId="dry-bulk-decision-text"
            label={t(`${LANG_BASE}.${approved ? "comments" : "reason"}`)}
          >
            {(approved ? decision.comments : decision.rejectReason) || "-"}
          </Row>
          <Row testId="dry-bulk-decision-files" label={t(`${LANG_BASE}.attachments`)}>
            {files.length === 0
              ? "-"
              : files.map((attachment) => (
                  <AttachmentFileLink
                    key={attachment.fileUrl}
                    className="block"
                    url={attachment.fileUrl}
                    name={attachment.fileName}
                  />
                ))}
          </Row>
        </div>
      </SideDrawer>
    </div>
  );
};

export default DecisionDetailsDrawer;
